import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const TORCHLIGHT_TICKETS_URL =
  'https://edwinterfest.com/hogmanay/whats-on/torchlight-procession';

const TODO_LINKS = {
  holyroodPark: 'https://www.historicenvironment.scot/visit-a-place/places/holyrood-park/',
  edinburghChristmas: 'https://www.edinburghschristmas.com/',
  botanicsLights: 'https://www.rbge.org.uk/visit/edinburgh-botanic-garden/',
  nc500: 'https://www.northcoast500.com/',
};

function TodoLink({ href, children, className }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className ?? 'hotels-modal__hotel-link'}>
      {children}
    </a>
  );
}

/**
 * Stay “To do” modal, same chrome as {@link HotelsModal} (tape, paper, close).
 * @param {{ open: boolean; onClose: () => void; dialogId: string; faqMarkerSrc: string; todoTitleArtSrc: string; tapeArtSrc: string; paperTextureSrc: string }} props
 */
export function StayTodoModal({ open, onClose, dialogId, faqMarkerSrc, todoTitleArtSrc, tapeArtSrc, paperTextureSrc }) {
  const titleId = useId();
  const closeBtnRef = useRef(null);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open, onKeyDown]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="hotels-modal" role="presentation">
      <button
        type="button"
        className="hotels-modal__backdrop"
        aria-label="Close to-do guide"
        onClick={onClose}
      />
      <div className="hotels-modal__panel-shell">
        <div className="hotels-modal__tape" aria-hidden="true">
          <img src={tapeArtSrc} alt="" className="hotels-modal__tape-img" decoding="async" />
        </div>
        <div
          id={dialogId}
          className="hotels-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="hotels-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div
            className="hotels-modal__body hotels-modal__body--todo"
            style={{
              '--hotels-modal-texture': `url("${new URL(paperTextureSrc, window.location.href).href}")`,
            }}
          >
            <div className="hotels-modal__intro-shell hotels-modal__intro-shell--todo">
              <div className="hotels-modal__intro">
                <h2 id={titleId} className="hotels-modal__title">
                  <img
                    src={todoTitleArtSrc}
                    alt="To do"
                    className="hotels-modal__title-art"
                    width="69"
                    height="64"
                    decoding="async"
                  />
                </h2>
              </div>
            </div>

            <h3 className="hotels-modal__subhead hotels-modal__subhead--decorated hotels-modal__subhead--todo">
              <span className="hotels-modal__subhead-row">
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
                <span className="hotels-modal__subhead-text">Suggestions</span>
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker hotels-modal__faq-marker--flip"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
              </span>
            </h3>

            <div className="hotels-modal__spotlights hotels-modal__spotlights--todo">
              <article className="hotels-modal__spotlight">
                <header className="hotels-modal__spotlight-head">
                  <TodoLink href={TORCHLIGHT_TICKETS_URL} className="hotels-modal__spotlight-title">
                    Hogmanay Torchlight
                  </TodoLink>
                  <span className="hotels-modal__tier-pill" aria-hidden="true">
                    29 Dec
                  </span>
                </header>
                <p className="hotels-modal__spotlight-detail">
                  Part of Edinburgh&apos;s winter festival, thousands of torch-bearers process across the city; this year
                  the route uses The Meadows. It&apos;s a memorable way to start the Hogmanay weekend early. The
                  procession begins at <strong>6:30pm</strong> on 29 December. Use the title link above for tickets and
                  the latest route information.
                </p>
              </article>

              <article className="hotels-modal__spotlight">
                <header className="hotels-modal__spotlight-head">
                  <TodoLink href={TODO_LINKS.botanicsLights} className="hotels-modal__spotlight-title">
                    Lights at botanics
                  </TodoLink>
                  <span className="hotels-modal__tier-pill" aria-hidden="true">
                    Evenings
                  </span>
                </header>
                <p className="hotels-modal__spotlight-detail">
                  The Royal Botanic Garden Edinburgh runs a popular after-dark light trail through the gardens on
                  Inverleith Row. It&apos;s mostly ticketed, timed entry, and often sells out, so book ahead if
                  you&apos;d like to go. The garden link above lists what&apos;s on and how to visit.
                </p>
              </article>

              <article className="hotels-modal__spotlight">
                <header className="hotels-modal__spotlight-head">
                  <TodoLink href={TODO_LINKS.holyroodPark} className="hotels-modal__spotlight-title">
                    Arthur&apos;s Seat
                  </TodoLink>
                  <span className="hotels-modal__tier-pill" aria-hidden="true">
                    Outdoors
                  </span>
                </header>
                <p className="hotels-modal__spotlight-detail">
                  Arthur&apos;s Seat is the rocky summit at the heart of Holyrood Park, with views over the city and the
                  Forth that are hard to beat. Allow roughly 45 minutes up and down from the park entrances near
                  Holyrood; paths can be steep and slippery in winter, so sturdy shoes and daylight are sensible. Entry
                  to the park is free.
                </p>
              </article>

              <article className="hotels-modal__spotlight">
                <header className="hotels-modal__spotlight-head">
                  <TodoLink href={TODO_LINKS.edinburghChristmas} className="hotels-modal__spotlight-title">
                    Festive markets
                  </TodoLink>
                  <span className="hotels-modal__tier-pill" aria-hidden="true">
                    Seasonal
                  </span>
                </header>
                <p className="hotels-modal__spotlight-detail">
                  Festive markets, food stalls, and rides take over the city centre around Princes Street, East Princes
                  Street Gardens, and George Street. Exact dates and attractions change each year, so check the official
                  site before you travel if you&apos;d like to plan an evening around it.
                </p>
              </article>

              <article className="hotels-modal__spotlight">
                <header className="hotels-modal__spotlight-head">
                  <TodoLink href={TODO_LINKS.nc500} className="hotels-modal__spotlight-title">
                    North Coast 500
                  </TodoLink>
                  <span className="hotels-modal__tier-pill" aria-hidden="true">
                    Road trip
                  </span>
                </header>
                <p className="hotels-modal__spotlight-detail">
                  Scotland&apos;s famous coastal loop through the Highlands is hard to top if you fancy an epic adventure
                  after the wedding. Ben proposed at Tongue, a small village right on the route, so this stretch of the
                  NC500 is especially close to our hearts. The full circuit is around 500 miles and most people spread it
                  across several days. Winter driving can be demanding, so plan carefully and check the weather.
                </p>
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
